<script setup lang="ts">
/*
 *
 * Imports
 *
 * */

//PrimeVue
import { default as PInputText } from 'primevue/inputtext'
import { default as PButton } from 'primevue/button'
import { default as PTag } from 'primevue/tag'

//Vue, router
import { computed, type ModelRef } from 'vue'

//Custom components and types
import CBaseHeader from '@/presentation/components/base/CBaseHeader.vue'
import type { LearningModuleInput } from '@/core/domain/learning/module/LearningModule.ts'
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus.ts'
import { useLearningModulesStore } from '@/presentation/stores/learning/learningModules.ts'
import { useLearningModuleAccessUtils } from '@/presentation/composables/learning/useLearningAccessUtils.ts'

// STORES
const learningModulesStore = useLearningModulesStore()

// Composables
const { canEditModule, canPublishModule } = useLearningModuleAccessUtils()

//Utils
const learningModule: ModelRef<LearningModuleInput> = defineModel('learningModule', {
  required: true
})
const fieldToUpdate: ModelRef<string | null> = defineModel('fieldToUpdate', {
  type: String,
  default: null
})

const emits = defineEmits(['cancel-update'])

const props = defineProps<{
  isCreating: boolean
  learningCourseTitle: string
  learningCourseId: number
}>()

const backToText = computed(() => {
  return `< Revenir au parcours "${props.learningCourseTitle}"`
})

function startUpdate(field: string) {
  fieldToUpdate.value = field
}

function cancelUpdate() {
  emits('cancel-update')
}

function validateTitleUpdate() {
  if (!props.isCreating) {
    saveModule()
  }

  fieldToUpdate.value = null
}

async function saveModule() {
  if (props.isCreating) return

  if (learningModule.value) {
    const updatedModule = await learningModulesStore.updateModule(
      learningModule.value,
      learningModule.value.id,
      props.learningCourseId
    )

    if (updatedModule) {
      learningModule.value = updatedModule.createLearningModuleInput()
      fieldToUpdate.value = null
    }
  }
}

async function publishModule() {
  if (learningModule.value) {
    const updatedModule = await learningModulesStore.updateModuleStatus(
      LearningStatus.Values.ACTIVE,
      learningModule.value.id,
      props.learningCourseId
    )

    if (updatedModule) {
      learningModule.value = updatedModule.createLearningModuleInput()
    }
  }
}
</script>

<template>
  <template v-if="fieldToUpdate === 'title'">
    <div class="border-surface col-span-12 flex gap-2 border-b bg-white p-4">
      <p-input-text v-model="learningModule.title" placeholder="Nom du module" class="grow" />
      <p-button
        severity="secondary"
        @click="cancelUpdate"
        label="Annuler"
        data-testid="cancel-update"
      />
      <p-button
        @click="validateTitleUpdate()"
        label="Valider"
        data-testid="validate-update-title"
      />
    </div>
  </template>
  <c-base-header
    v-else
    :display-breadcrumbs="false"
    has-widget
    class="border-surface border-b bg-white pb-2"
  >
    <template #breadcrumbs>
      <router-link
        v-if="backToText"
        :to="{ name: 'learning-course-update', params: { courseId: learningCourseId } }"
        class="text-muted-color no-underline"
        >{{ backToText }}</router-link
      >
    </template>

    <template #title>
      {{ learningModule.title }}
      <p-button
        text
        icon="pi pi-pencil"
        :disabled="!canEditModule(learningModule)"
        @click="startUpdate('title')"
        severity="secondary"
      />
      <p-tag
        :severity="LearningStatus.color(learningModule.status)"
        :value="LearningStatus.label(learningModule.status)"
      />
    </template>

    <template #widgets>
      <div class="col-span-4 flex h-full items-center justify-end gap-4">
        <p-button
          label="Publier le module"
          class="whitespace-nowrap"
          @click="publishModule()"
          :disabled="!learningModule.canBePublished || !canPublishModule(learningModule)"
          size="small"
        />
      </div>
    </template>
  </c-base-header>
</template>
